import { ReactNode, useEffect } from 'react';
import { Link } from 'react-router';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { Menu } from 'lucide-react';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { GateBanner } from './GateBanner';
import CookieConsent from './CookieConsent';
// PROJECT-289 T0289.9 — the feedback panel. Mounted HERE, in the layout that wraps every public
// page, because the PO's requirement is that a visitor can comment on the part of the page they
// are looking at — on any page, not only the landing page.
import FeedbackPopup from './FeedbackPopup';
import { applyAnalyticsConsent } from '@/config/firebase';
import { applyPosthogConsent } from '@/config/posthog';
import { LEGAL_CONFIG, LEGAL_ROUTES } from '../pages/legalConfig';

interface PublicLayoutProps {
  children: ReactNode;
}

const PublicLayoutInner = ({ children }: PublicLayoutProps) => {
  const { user } = usePublicAuth();

  // Initialize AOS animations
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: 'ease-in-out-cubic',
      once: true,
      offset: 100,
    });
  }, []);

  // Add JSON-LD structured data for organization
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Raisy',
      url: 'https://raisy.team',
      logo: 'https://raisy.team/logo.png',
      description: 'AI Workforce Platform. Raisy builds your processes — fundraising, sales, recruiting, operations — run by hundreds of specialized bots, with human oversight and full audit trails. Bots do the toil; people make the calls.',
      sameAs: [
        'https://twitter.com/raisy',
        'https://linkedin.com/company/raisy',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Service',
        email: `mailto:${LEGAL_CONFIG.contactEmail}`,
      },
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      {/* Gate banner + nav stick together as one sticky unit — avoids
          height-measurement bookkeeping between two separately-sticky siblings.
          The banner names whichever gate is open (today: the closed beta). */}
      <div className="sticky top-0 z-50">
        <GateBanner />

        {/* Navigation */}
        <nav className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/logo.png"
                alt="Raisy Logo"
                className="w-8 h-8"
              />
              <span className="font-bold text-lg text-slate-900 dark:text-white">Raisy</span>
            </Link>

            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white">
                Home
              </Link>
              <Link to="/about" className="text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white">
                About
              </Link>
              <Link to="/compare" className="text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white">
                Compare
              </Link>
              <Link to="/blog" className="text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white">
                Blog
              </Link>
            </div>

            {/* CTA Buttons + Theme Toggle */}
            <div className="flex items-center gap-2">
              <ThemeToggle />

              {/* Mobile nav menu */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[260px]">
                  <SheetHeader>
                    <SheetTitle>Menu</SheetTitle>
                  </SheetHeader>
                  <nav className="flex flex-col gap-4 mt-6">
                    <Link
                      to="/"
                      className="text-base font-medium text-slate-900 dark:text-white"
                    >
                      Home
                    </Link>
                    <Link
                      to="/about"
                      className="text-base font-medium text-slate-900 dark:text-white"
                    >
                      About
                    </Link>
                    <Link
                      to="/compare"
                      className="text-base font-medium text-slate-900 dark:text-white"
                    >
                      Compare
                    </Link>
                    <Link
                      to="/blog"
                      className="text-base font-medium text-slate-900 dark:text-white"
                    >
                      Blog
                    </Link>
                    <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                      {/* P100 D-001 — a SIGNED-OUT entry link must not go through the auth wall. This
                          pointed at `/entities`, a private route, so react-admin's guard ran first: it
                          stored the bounce destination and the visitor watched an auth detour (measured
                          on DEV 2026-09-28: ~0.9s of "Loading your workspace…" on a cold load) before
                          the login page he was going to be shown anyway. `/login` is public, and the
                          login page already lands him on `/entities` after signing in. Signed in, he
                          still belongs on the members area. */}
                      <Link to={user ? "/entities" : "/login"}>
                        <Button variant="outline" className="w-full">
                          Sign In
                        </Button>
                      </Link>
                      <Link to="/request-access">
                        <Button className="w-full bg-cyan-600 hover:bg-cyan-700">
                          Request Access
                        </Button>
                      </Link>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>

              {user ? (
                <div className="hidden md:flex items-center gap-2">
                  <Link to="/entities">
                    <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700">
                      Dashboard
                    </Button>
                  </Link>
                  <Link
                    to="/entities"
                    title={user.displayName || 'Dashboard'}
                    className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <Avatar className="h-8 w-8 cursor-pointer ring-2 ring-cyan-500/30 ring-offset-2 ring-offset-background">
                      <AvatarImage
                        src={user.photoURL || undefined}
                        alt={user.displayName || 'User'}
                      />
                      <AvatarFallback>
                        {(
                          user.displayName?.charAt(0) ||
                          user.email?.charAt(0) ||
                          'U'
                        ).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                </div>
              ) : (
                <div className="hidden md:flex items-center gap-2">
                  {/* P100 D-001 — same rule as the mobile entry above: signed out, this is `/login`
                      (public), never `/entities` (private, so the guard detours him). */}
                  <Link to="/login">
                    <Button variant="outline" size="sm">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/request-access">
                    <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700">
                      Request Access
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-x-hidden">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Raisy</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                AI Workforce Platform. Bots do the toil; your people make the calls.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li><Link to="/about" className="hover:text-slate-900 dark:hover:text-white">About</Link></li>
                <li><Link to="/compare" className="hover:text-slate-900 dark:hover:text-white">Compare</Link></li>
                <li><Link to="/pitch" className="hover:text-slate-900 dark:hover:text-white">Pitch Deck</Link></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-white">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Docs</h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-white">Getting Started</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-white">API</a></li>
                <li><a href="#" className="hover:text-slate-900 dark:hover:text-white">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li><Link to={LEGAL_ROUTES.privacy} className="hover:text-slate-900 dark:hover:text-white">Privacy</Link></li>
                <li><Link to={LEGAL_ROUTES.terms} className="hover:text-slate-900 dark:hover:text-white">Terms</Link></li>
                <li><Link to={LEGAL_ROUTES.cookie} className="hover:text-slate-900 dark:hover:text-white">Cookies</Link></li>
                <li><Link to={LEGAL_ROUTES.dmca} className="hover:text-slate-900 dark:hover:text-white">DMCA</Link></li>
                <li><Link to={LEGAL_ROUTES.accessibility} className="hover:text-slate-900 dark:hover:text-white">Accessibility</Link></li>
                <li><Link to={LEGAL_ROUTES.contact} className="hover:text-slate-900 dark:hover:text-white">Contact</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 dark:border-slate-700 pt-8 text-center text-sm text-slate-600 dark:text-slate-400">
            <p>&copy; 2026 Raisy. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <CookieConsent
        privacyUrl={LEGAL_ROUTES.privacy}
        cookiePolicyUrl={LEGAL_ROUTES.cookie}
        onConsentChange={(consent) => {
          applyAnalyticsConsent(consent);
          void applyPosthogConsent(consent);
        }}
        texts={{
          title: 'Cookie preferences',
          description:
            'We use cookies to operate the site, personalize content, and analyze traffic. Read more in our {privacyLink} and {cookiePolicyLink}.',
          privacyLinkText: 'Privacy Notice',
          cookiePolicyLinkText: 'Cookie Policy',
          categories: {
            necessary: {
              name: 'Necessary',
              description: 'Essential for the site to work (login, security, session management).',
            },
            personalization: {
              name: 'Personalization',
              description: 'Remember your preferences and settings.',
            },
            marketing: {
              name: 'Marketing',
              description: 'Would be used to deliver relevant advertisements if we run campaigns.',
            },
            statistics: {
              name: 'Statistics',
              description: 'Understand how visitors interact with our Services via Firebase Analytics.',
            },
          },
          buttons: {
            acceptAll: 'Accept all',
            acceptNecessary: 'Reject non-essential',
            saveSelection: 'Save selection',
            managePreferences: 'Manage preferences',
            collapse: 'Collapse',
          },
        }}
      />
      {/* PROJECT-289 T0289.9 — the feedback panel, mounted beside the cookie banner because it is
          the same kind of thing: a small floating surface that follows the visitor across the
          public pages. */}
      <FeedbackPopup />
    </div>
  );
};

export const PublicLayout = ({ children }: PublicLayoutProps) => (
  <PublicLayoutInner>{children}</PublicLayoutInner>
);
